import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  Bell, 
  MessageSquare, 
  Mail, 
  CheckCircle, 
  ArrowRight, 
  Clock, 
  Calendar, 
  Settings,
  Smartphone
} from 'lucide-react';
import { Link } from 'react-router-dom';

const PatientRemindersPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-blue-100/70"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                <span>Communication Suite</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Reduce No-Shows with
                <span className="bg-gradient-to-r from-blue-900 via-blue-600 to-blue-400 bg-clip-text text-transparent"> Automated Patient Reminders</span>
              </h1>
              
              <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                Intelligent, multi-channel appointment reminders that actually get read. Customize the timing, cadence, and content for every appointment type.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/book-demo">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 rounded-xl text-lg transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-200">
                    Get a Demo
                  </Button>
                </Link>
                <Button variant="outline" className="px-8 py-6 rounded-xl text-lg border-2 hover:bg-gray-50">
                  See Pricing
                </Button>
              </div>
            </div>
            
            {/* Right: Visual */}
            <div className="relative h-[600px] w-full flex items-center justify-center">
                <div className="w-full max-w-sm relative">
                    {/* Phone Mockup */}
                    <div className="bg-slate-900 rounded-[3rem] p-4 shadow-2xl border-4 border-slate-800 relative z-10">
                        <div className="bg-white rounded-[2.5rem] overflow-hidden h-[500px] relative">
                            {/* Header */}
                            <div className="bg-slate-50 p-6 pt-12 pb-4 border-b border-slate-100 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                                    AD
                                </div>
                                <div>
                                    <div className="font-bold text-slate-800">Asha Dental</div>
                                    <div className="text-xs text-slate-500">To: You</div>
                                </div>
                            </div>

                            {/* Messages */}
                            <div className="p-4 space-y-4">
                                <div className="bg-slate-100 p-3 rounded-2xl rounded-tl-none text-sm text-slate-700 max-w-[85%]">
                                    Hi Sarah! You have an appointment with Dr. Smith tomorrow at 2:00 PM.
                                </div>
                                <div className="bg-slate-100 p-3 rounded-2xl rounded-tl-none text-sm text-slate-700 max-w-[85%]">
                                    Please reply C to confirm or R to reschedule.
                                </div>
                                
                                <div className="flex justify-end">
                                    <div className="bg-blue-600 p-3 rounded-2xl rounded-tr-none text-sm text-white font-bold">
                                        C
                                    </div>
                                </div>

                                <div className="bg-slate-100 p-3 rounded-2xl rounded-tl-none text-sm text-slate-700 max-w-[85%]">
                                    Great! You are confirmed. Please remember to bring your new insurance card. See you soon! 👋
                                </div>
                            </div>

                            {/* Input Area */}
                            <div className="absolute bottom-0 left-0 w-full p-4 bg-slate-50 border-t border-slate-100">
                                <div className="h-10 bg-white border border-slate-200 rounded-full w-full"></div>
                            </div>
                        </div>
                    </div>

                    {/* Floating Elements */}
                    <div className="absolute top-20 -right-12 bg-white p-3 rounded-xl shadow-lg animate-bounce">
                        <Bell className="w-6 h-6 text-yellow-500" />
                    </div>
                    <div className="absolute bottom-40 -left-8 bg-white p-3 rounded-xl shadow-lg animate-pulse">
                        <CheckCircle className="w-6 h-6 text-green-500" />
                    </div>
                </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Cards Section */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Complete Control
            </h2>
            <p className="text-lg text-gray-600">
              Not all appointments are equal. Customize your outreach strategy to match the importance of the visit.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-blue-600 group-hover:scale-110 transition-transform">
                <Settings className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Custom Cadence</h3>
              <p className="text-gray-600">
                Set reminders for 2 weeks, 2 days, and 2 hours before. Adjust based on appointment type (e.g., more reminders for big cases).
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-purple-600 group-hover:scale-110 transition-transform">
                <Smartphone className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Multi-Channel</h3>
              <p className="text-gray-600">
                Reach patients via SMS, Email, or Voice Call. If they don't respond to one, try another automatically.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-green-600 group-hover:scale-110 transition-transform">
                <Calendar className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Native Schedule Updates</h3>
              <p className="text-gray-600">
                When a patient confirms, the appointment status updates instantly in Asha PMS after migration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Visualization */}
      <section className="py-24 px-6 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2"></div>

        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-blue-400 font-bold tracking-wider text-xs uppercase mb-4">
                <Clock className="w-4 h-4" />
                The Sequence
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                Perfect Timing
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                A typical high-value appointment cadence ensures top-of-mind awareness without being annoying.
              </p>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 font-bold">1</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Save the Date</h4>
                    <p className="text-slate-400 text-sm">Sent immediately upon booking (Email + Calendar Invite).</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0 font-bold">2</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">The Nudge</h4>
                    <p className="text-slate-400 text-sm">3 Days Before: Friendly SMS reminder to confirm.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0 font-bold">3</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">The Prep</h4>
                    <p className="text-slate-400 text-sm">1 Day Before: Pre-arrival instructions (e.g., fasting) sent via Email.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 font-bold">4</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">The Final Check</h4>
                    <p className="text-slate-400 text-sm">2 Hours Before: SMS with directions link.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
               <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-8 border border-slate-700 shadow-2xl">
                  <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center">
                              <Calendar className="w-5 h-5 text-blue-400" />
                          </div>
                          <div>
                              <div className="font-bold text-white">Confirmation Rate</div>
                              <div className="text-xs text-slate-400">Last 30 Days</div>
                          </div>
                      </div>
                      <div className="text-2xl font-bold text-green-400">92%</div>
                  </div>

                  {/* Bar Chart Visualization */}
                  <div className="flex items-end justify-between gap-2 h-32 mb-4 px-2">
                      <div className="w-full bg-slate-700/50 rounded-t h-[40%]"></div>
                      <div className="w-full bg-slate-700/50 rounded-t h-[60%]"></div>
                      <div className="w-full bg-slate-700/50 rounded-t h-[55%]"></div>
                      <div className="w-full bg-slate-700/50 rounded-t h-[75%]"></div>
                      <div className="w-full bg-slate-700/50 rounded-t h-[80%]"></div>
                      <div className="w-full bg-blue-600 rounded-t h-[92%] relative">
                          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-bold text-white">Today</div>
                      </div>
                  </div>
                  
                  <div className="text-center text-xs text-slate-500">
                      Industry Average: 65% vs Your Practice: 92%
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Stats */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-blue-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">-25%</div>
              <div className="text-gray-600 font-medium">Fewer No-Shows</div>
            </div>
            <div className="bg-indigo-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-indigo-600 mb-2">10 hrs</div>
              <div className="text-gray-600 font-medium">Saved Weekly on Calls</div>
            </div>
            <div className="bg-purple-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">100%</div>
              <div className="text-gray-600 font-medium">Automated</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Keep your schedule full.
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Stop calling patients to confirm appointments. Let our automated system do the heavy lifting.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-demo">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-6 rounded-full text-lg shadow-xl shadow-blue-200 hover:shadow-blue-300 hover:-translate-y-1 transition-all">
                Automate Reminders
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PatientRemindersPage;
